/**
 * PlanBadge — Animated plan badge for Free / Pro / Enterprise.
 * Pro and Enterprise have a gradient shimmer animation.
 */

import React from "react";
import { Shield, Zap, Crown } from "lucide-react";
import { cn } from "../../lib/utils";
import type { PlanName } from "../../api/subscriptions";

interface PlanBadgeProps {
  plan: PlanName;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
  className?: string;
}

const CONFIG: Record<
  PlanName,
  {
    label: string;
    icon: React.ElementType;
    iconClass: string;
    containerClass: string;
    labelClass: string;
    shimmer: boolean;
  }
> = {
  free: {
    label: "Community",
    icon: Shield,
    iconClass: "text-slate-400",
    containerClass: "bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700",
    labelClass: "text-slate-600 dark:text-slate-300",
    shimmer: false,
  },
  starter: {
    label: "Starter",
    icon: Zap,
    iconClass: "text-emerald-500",
    containerClass:
      "bg-emerald-500/10 border border-emerald-400/30 dark:border-emerald-500/40",
    labelClass: "text-emerald-700 dark:text-emerald-300 font-semibold",
    shimmer: false,
  },
  growth: {
    label: "Growth",
    icon: Zap,
    iconClass: "text-emerald-600",
    containerClass:
      "bg-gradient-to-r from-emerald-500/10 via-emerald-500/10 to-emerald-500/10 border border-emerald-400/30 dark:border-emerald-500/40",
    labelClass: "text-emerald-700 dark:text-emerald-300 font-semibold",
    shimmer: true,
  },
  enterprise: {
    label: "Enterprise",
    icon: Crown,
    iconClass: "text-amber-500",
    containerClass:
      "bg-gradient-to-r from-amber-500/10 via-orange-400/10 to-amber-500/10 border border-amber-400/40 dark:border-amber-500/40",
    labelClass: "text-amber-700 dark:text-amber-300 font-semibold",
    shimmer: true,
  },
};

const SIZE_MAP = {
  sm: { badge: "px-2 py-0.5 text-xs rounded-md gap-1", icon: "h-3 w-3" },
  md: { badge: "px-3 py-1 text-sm rounded-lg gap-1.5", icon: "h-4 w-4" },
  lg: { badge: "px-4 py-1.5 text-base rounded-xl gap-2", icon: "h-5 w-5" },
};

export default function PlanBadge({
  plan,
  size = "md",
  showLabel = true,
  className,
}: PlanBadgeProps) {
  const cfg = CONFIG[plan];
  const sz = SIZE_MAP[size];
  const Icon = cfg.icon;

  return (
    <span
      className={cn(
        "inline-flex items-center font-medium transition-all",
        cfg.containerClass,
        sz.badge,
        cfg.shimmer && "plan-badge-shimmer",
        className
      )}
    >
      <Icon className={cn(sz.icon, cfg.iconClass)} />
      {showLabel && <span className={cfg.labelClass}>{cfg.label}</span>}

      {/* Shimmer keyframe injected once via style tag */}
      {cfg.shimmer && (
        <style>{`
          @keyframes planShimmer {
            0%   { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          .plan-badge-shimmer {
            background-size: 200% auto;
            animation: planShimmer 3s linear infinite;
          }
        `}</style>
      )}
    </span>
  );
}
